import { useEffect, useMemo, useState } from "react";
import { useCustom, useCustomMutation, useGetLocale, usePermissions, useTranslate } from "@refinedev/core";
import { Button, CheckboxField, Field, Modal, PencilIcon, SelectField, StatusBadge, type Column, type StatusTone } from "@fawkes/ui";

import { BtwKeuze, btwWeergave, huidigeBtwUitHistorie, useBtwCodes, type BtwCode } from "../../components/BtwKeuze";
import { idOf, keuzes, labelVan, useFinancieleOpties, type FinancieleOpties } from "./financieleOpties";
import type { VestigingKeuze } from "../../components/useVestigingKeuze";
import { formatDate, formatEuro, todayIso } from "../../i18n/format";
import { AccommodatieIcoon, type Prijsberekening } from "../pensionplanning/accommodatie";
import { IndeelbareTabel } from "../../components/IndeelbareTabel";

// Producten › Pensiontarieven › Accommodaties: de prijs per dag van elk accommodatietype, in dezelfde versieketen
// als de pensionprijzen (tarief_versie, type PENSION_ACCOMMODATIE): laag- en hoogseizoen, vanaf een ingangsdatum,
// met btw. Of die prijs de dagprijs van het verblijf is of een toeslag bovenop de pensionprijs, kiest de vestiging
// in Instellingen › Pension › Prijsberekening; de types zelf maakt het pension aan bij de kennelplanning.

interface AccommodatieTariefRij {
  accommodatieId: number;
  vestigingId: number;
  diersoortId: number | null;
  diersoortNaam: string | null;
  naam: string;
  icoon: string | null;
  afbeelding: string | null;
  kleur: string;
  luxe: boolean;
  quarantaine: boolean;
  dagprijs: number | null;
  dagprijsHoog: number | null;
  geldigVanaf: string | null;
  laatstGewijzigd: string | null;
  btwCodeId: number | null;
  isNieuw: boolean;
  isHoogseizoenOpPeildatum: boolean;
}

interface HistorieRegel {
  versieId: number;
  bedrag: number;
  bedragHoog: number | null;
  effectdatum: string;
  einddatum: string | null;
  reden: string | null;
  btwCodeId: number | null;
  /** Waar deze prijs op geboekt werd; leeg bij versies van voor deze velden bestonden. */
  grootboekRekeningId: number | null;
  kostenPlaatsId: number | null;
  projectCodeId: number | null;
  status: "GEPLAND" | "ACTIEF" | "VERVALLEN";
}

type T = (key: string, options?: { args?: unknown[] }) => string;
type Melding = "error" | "forbidden" | "forbiddenPast" | null;

const getal = (v: string): number | null => {
  if (v.trim() === "") return null;
  const n = Number(v.replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

const toon = (status: HistorieRegel["status"]): StatusTone =>
  status === "ACTIEF" ? "success" : status === "GEPLAND" ? "warning" : "neutral";

/**
 * Een nieuw accommodatietype.
 *
 * <p>Het type komt in dezelfde tabel als waar de kennelplanning uit leest, dus wat je hier aanmaakt
 * staat daar meteen. Alleen het type: de ruimtes zelf blijven bij de planning, want die gaan over
 * hoeveel hokken er zijn en waar ze staan, en dat is een andere vraag dan wat een verblijf kost.
 *
 * <p>Bewust klein gehouden: naam, diersoort en de twee vlaggen die de planning gebruikt. Het icoon, de
 * kleur en de capaciteitsregels staan in het beheerscherm bij de planning; die hier ook aanbieden zou
 * twee plekken opleveren die hetzelfde doen en uit de pas gaan lopen.
 */
function NieuwAccommodatietype({ vestigingId, t, onSluit, onAangemaakt }: {
  vestigingId: number | null;
  t: T;
  onSluit: () => void;
  onAangemaakt: () => void;
}) {
  const soorten = useCustom<{ id: number; label: string }[]>({
    url: "animals/reference/species",
    method: "get",
  });
  const lijst = Array.isArray(soorten.data?.data) ? soorten.data.data : [];
  const { mutate, isLoading: bezig } = useCustomMutation();

  const [naam, setNaam] = useState("");
  const [diersoortId, setDiersoortId] = useState("");
  const [omschrijving, setOmschrijving] = useState("");
  const [luxe, setLuxe] = useState(false);
  const [quarantaine, setQuarantaine] = useState(false);
  const [fout, setFout] = useState<string | null>(null);

  const bewaar = () => {
    if (naam.trim() === "" || diersoortId === "") {
      setFout(t("pensionTarief.accommodatie.nieuw.verplicht"));
      return;
    }
    setFout(null);
    mutate(
      {
        url: `pension/accommodatietypes${vestigingId == null ? "" : `?vestigingId=${vestigingId}`}`,
        method: "post",
        values: {
          naam: naam.trim(),
          diersoortId: Number(diersoortId),
          omschrijving: omschrijving.trim() === "" ? null : omschrijving.trim(),
          luxe,
          quarantaine,
        },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: onAangemaakt,
        // 409 betekent dat de naam al bestaat binnen deze vestiging; dat is de enige fout die de
        // gebruiker zelf kan verhelpen, dus die krijgt zijn eigen zin.
        onError: (err: unknown) =>
          setFout((err as { statusCode?: number })?.statusCode === 409
            ? t("pensionTarief.accommodatie.nieuw.bestaat")
            : t("pensionTarief.accommodatie.nieuw.fout")),
      },
    );
  };

  return (
    <Modal open title={t("pensionTarief.accommodatie.nieuw.titel")} onClose={onSluit}
      footer={
        <>
          <Button variant="secondary" onClick={onSluit}>{t("common.cancel")}</Button>
          <Button onClick={bewaar} disabled={bezig}>{t("common.save")}</Button>
        </>
      }
    >
      <div className="asiel-tarief__form">
        <Field label={t("pensionTarief.accommodatie.nieuw.naam")} value={naam}
          onChange={(e) => setNaam(e.target.value)} />
        <SelectField
          label={t("pensionTarief.accommodatie.nieuw.diersoort")}
          value={diersoortId}
          onChange={(e) => setDiersoortId(e.target.value)}
          options={[{ value: "", label: t("pensionTarief.accommodatie.nieuw.kiesSoort") },
            ...lijst.map((d) => ({ value: String(d.id), label: d.label }))]}
        />
        <Field label={t("pensionTarief.accommodatie.nieuw.omschrijving")} value={omschrijving}
          onChange={(e) => setOmschrijving(e.target.value)} />
        <CheckboxField label={t("pensionTarief.accommodatie.nieuw.luxe")} checked={luxe}
          onChange={(e) => setLuxe(e.target.checked)} />
        <CheckboxField label={t("pensionTarief.accommodatie.nieuw.quarantaine")} checked={quarantaine}
          onChange={(e) => setQuarantaine(e.target.checked)} />
        <p className="pt-nieuwtype__hint">{t("pensionTarief.accommodatie.nieuw.hint")}</p>
        {fout ? <p className="ad-venster__fout" role="alert">{fout}</p> : null}
      </div>
    </Modal>
  );
}

export function AccommodatieTarievenTab({ vestiging }: { vestiging: VestigingKeuze }) {
  const t = useTranslate() as T;
  const locale = useGetLocale()() ?? "nl";
  const { data, isLoading, isError, refetch } = useCustom<AccommodatieTariefRij[]>({
    url: "pension-tarief/accommodaties",
    method: "get",
  });
  const instelling = useCustom<{ prijsberekening: Prijsberekening }>({
    url: "pension/prijsberekening",
    method: "get",
    queryOptions: { retry: false },
  });
  const berekening: Prijsberekening = instelling.data?.data?.prijsberekening ?? "PENSIONPRIJZEN";
  /**
   * Kent dit prijsmodel seizoenen?
   *
   * <p>Bij dagprijzen per accommodatie niet: daar is de kalender waar je een drukke week duurder maakt,
   * en een tweede mechanisme ernaast zou betekenen dat niemand meer kan zien waar een prijs vandaan
   * komt. Bij pensionprijzen is de prijs van dit type een toeslag op het tarief van het dier, en die
   * volgt wél de seizoenen van dat tarief.
   */
  const kentSeizoen = berekening !== "ACCOMMODATIE";
  // Aanmaken is pensionbeheer, net als in de api.
  const { data: rechten } = usePermissions<string[]>();
  const magTypeAanmaken = (rechten ?? []).includes("ROLE_PENSION_ADMIN");
  const [nieuwOpen, setNieuwOpen] = useState(false);
  const rijen = useMemo(
    () => (Array.isArray(data?.data) ? data.data : []).filter((r) => vestiging.hoortErbij(r)),
    [data, vestiging],
  );
  const [bewerk, setBewerk] = useState<AccommodatieTariefRij | null>(null);

  const bedrag = (waarde: number | null) => (waarde != null ? formatEuro(waarde, locale) : t("pensionTarief.accommodatie.geenPrijs"));

  const kolommen: Column<AccommodatieTariefRij>[] = [
    {
      header: t("pensionTarief.accommodatie.col.diersoort"),
      cell: (r) => (r.diersoortNaam ? <StatusBadge tone="neutral">{r.diersoortNaam}</StatusBadge> : "—"),
    },
    {
      header: t("pensionTarief.accommodatie.col.naam"),
      cell: (r) => (
        <span className="pension-tarief__nieuw-cell">
          <AccommodatieIcoon type={r} size={18} />
          {r.naam}
          {r.luxe && <StatusBadge tone="info">{t("pensionTarief.accommodatie.luxe")}</StatusBadge>}
          {r.quarantaine && <StatusBadge tone="danger">{t("pensionTarief.accommodatie.quarantaine")}</StatusBadge>}
        </span>
      ),
    },
    { header: t(`pensionTarief.accommodatie.col.laag.${berekening}`), cell: (r) => bedrag(r.dagprijs) },
    ...(kentSeizoen
      ? [{
          header: t(`pensionTarief.accommodatie.col.hoog.${berekening}`),
          cell: (r: AccommodatieTariefRij) => bedrag(r.dagprijsHoog),
        }]
      : []),
    {
      header: t("pensionTarief.accommodatie.col.geldigVanaf"),
      cell: (r) => (r.geldigVanaf ? formatDate(r.geldigVanaf, locale) : "—"),
    },
    {
      header: t("pensionTarief.accommodatie.col.acties"),
      cell: (r) => (
        <button
          type="button"
          className="tarief-rijactie"
          onClick={() => setBewerk(r)}
          title={t("pensionTarief.accommodatie.wijzig")}
          aria-label={t("pensionTarief.accommodatie.wijzigVan", { args: [r.naam] })}
        >
          <PencilIcon width={16} height={16} />
        </button>
      ),
    },
  ];

  return (
    <div className="asiel-tarief__panel">
      {/* Hier stond een uitlegbanner over het prijsmodel. Weg op verzoek: de kolomkoppen zeggen het al
          -- "Dagprijs" bij rekenen per accommodatie, "Toeslag laagseizoen" bij rekenen per dier -- en een
          balk die bij elk bezoek hetzelfde uitlegt wordt na de tweede keer niet meer gelezen. */}
      {magTypeAanmaken ? (
        <div className="pt-nieuwtype__balk">
          <Button onClick={() => setNieuwOpen(true)}>
            {t("pensionTarief.accommodatie.nieuw.knop")}
          </Button>
        </div>
      ) : null}
      {nieuwOpen ? (
        <NieuwAccommodatietype
          vestigingId={vestiging.gekozenIds.size === 1 ? [...vestiging.gekozenIds][0] : null}
          t={t}
          onSluit={() => setNieuwOpen(false)}
          onAangemaakt={() => { setNieuwOpen(false); refetch(); }}
        />
      ) : null}
      {isError ? (
        <div className="asiel-tarief__notice asiel-tarief__notice--error">{t("pensionTarief.accommodatie.laden")}</div>
      ) : (
        <IndeelbareTabel tabelKey="pensiontarief-accommodatie-tarieven-tab-1"
          columns={kolommen}
          rows={rijen}
          getRowKey={(r) => String(r.accommodatieId)}
          empty={isLoading ? t("common.loading") : t("pensionTarief.accommodatie.leeg")}
        />
      )}
      {bewerk && (
        <PrijsVenster
          rij={bewerk}
          berekening={berekening}
          t={t}
          locale={locale}
          onSluit={() => setBewerk(null)}
          onOpgeslagen={() => void refetch()}
        />
      )}
    </div>
  );
}

interface PrijsRegel {
  effectdatum: string;
  bedrag: string;
  bedragHoog: string;
  reden: string;
  btwCodeId: string;
  grootboekRekeningId: string;
  kostenPlaatsId: string;
  projectCodeId: string;
}

function PrijsVenster({
  rij,
  berekening,
  t,
  locale,
  onSluit,
  onOpgeslagen,
}: {
  rij: AccommodatieTariefRij;
  berekening: Prijsberekening;
  t: T;
  locale: string;
  onSluit: () => void;
  onOpgeslagen: () => void;
}) {
  // Zelfde afweging als in de lijst: dagprijzen per accommodatie kennen geen seizoenen.
  const kentSeizoen = berekening !== "ACCOMMODATIE";
  const historieUrl = `pension-tarief/accommodaties/${rij.accommodatieId}/historie`;
  const { data, isLoading, refetch } = useCustom<HistorieRegel[]>({ url: historieUrl, method: "get" });
  const historie = useMemo(() => (Array.isArray(data?.data) ? data.data : []), [data]);
  const btwCodes = useBtwCodes();
  const huidigeBtw = huidigeBtwUitHistorie(historie);
  const opties = useFinancieleOpties();
  // Een nieuwe regel begint bij wat er nu geldt: een prijswijziging verplaatst de boeking zelden, en
  // drie keuzelijsten opnieuw invullen voor dezelfde grootboekrekening is werk zonder opbrengst.
  const lopend = useMemo(() => historie.find((h) => h.status === "ACTIEF") ?? historie[0] ?? null, [historie]);
  const tekst = (n: number | null | undefined) => (n == null ? "" : String(n));
  const { mutate, isLoading: bezig } = useCustomMutation();
  const [regels, setRegels] = useState<PrijsRegel[]>([]);
  const [melding, setMelding] = useState<Melding>(null);
  const [btwOntbreekt, setBtwOntbreekt] = useState(false);
  const [opgeslagen, setOpgeslagen] = useState(false);
  /** Welke regel er in het geneste venster staat: een index, "nieuw", of niets. */
  const [bewerkt, setBewerkt] = useState<number | "nieuw" | null>(null);

  useEffect(() => {
    if (huidigeBtw === "") return;
    setRegels((lijst) => lijst.map((r) => (r.btwCodeId === "" ? { ...r, btwCodeId: huidigeBtw } : r)));
  }, [huidigeBtw]);

  /** Een lege regel, met de boeking en de btw van wat er nu geldt. */
  const legeRegel = (): PrijsRegel => ({
    effectdatum: todayIso(), bedrag: "", bedragHoog: "", reden: "", btwCodeId: huidigeBtw,
    grootboekRekeningId: tekst(lopend?.grootboekRekeningId),
    kostenPlaatsId: tekst(lopend?.kostenPlaatsId),
    projectCodeId: tekst(lopend?.projectCodeId),
  });

  /** De tweede regel van een item in de lijst: btw, boeking en reden, zonder de lege ervan. */
  const bijschrift = (r: PrijsRegel) =>
    [btwWeergave(btwCodes, r.btwCodeId === "" ? null : Number(r.btwCodeId)),
     labelVan(opties.grootboekRekeningen, idOf(r.grootboekRekeningId)),
     r.reden.trim() === "" ? null : r.reden.trim()]
      .filter((v) => v && v !== "—")
      .join(" · ");

  /** Het bedrag van een item, met het hoogseizoenbedrag erachter als dat model dat kent. */
  const samenvatting = (r: PrijsRegel) => {
    const laag = getal(r.bedrag);
    const hoog = getal(r.bedragHoog);
    const eerste = laag == null ? "—" : formatEuro(laag, locale);
    return kentSeizoen && hoog != null ? eerste + " / " + formatEuro(hoog, locale) : eerste;
  };

  const bewaar = () => {
    const geldig = regels.filter((r) => r.effectdatum !== "" && getal(r.bedrag) != null);
    if (geldig.length === 0) {
      setMelding("error");
      return;
    }
    if (geldig.some((r) => r.btwCodeId === "")) {
      setBtwOntbreekt(true);
      return;
    }
    setBtwOntbreekt(false);
    setMelding(null);
    setOpgeslagen(false);
    const verleden = geldig.some((r) => r.effectdatum < todayIso());
    const regel = (r: PrijsRegel) => ({
      effectdatum: r.effectdatum,
      bedrag: getal(r.bedrag),
      bedragHoog: getal(r.bedragHoog),
      btwCodeId: Number(r.btwCodeId),
      reden: r.reden.trim() === "" ? null : r.reden.trim(),
      grootboekRekeningId: idOf(r.grootboekRekeningId),
      kostenPlaatsId: idOf(r.kostenPlaatsId),
      projectCodeId: idOf(r.projectCodeId),
    });
    mutate(
      {
        url: `pension-tarief/accommodaties/${rij.accommodatieId}/prijswijziging`,
        method: "post",
        values: geldig.length === 1 ? regel(geldig[0]) : { geplandeWijzigingen: geldig.map(regel) },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          setRegels([]);
          setOpgeslagen(true);
          void refetch();
          onOpgeslagen();
        },
        onError: (err) => {
          const code = (err as { statusCode?: number }).statusCode;
          setMelding(code === 403 ? (verleden ? "forbiddenPast" : "forbidden") : "error");
        },
      },
    );
  };

  const kolommen: Column<HistorieRegel>[] = [
    { header: t("pensionTarief.hist.vanaf"), cell: (r) => formatDate(r.effectdatum, locale) },
    { header: t("pensionTarief.hist.tot"), cell: (r) => formatDate(r.einddatum, locale) },
    { header: t(`pensionTarief.accommodatie.col.laag.${berekening}`), align: "right", cell: (r) => formatEuro(r.bedrag, locale) },
    ...(kentSeizoen
      ? [{
          header: t(`pensionTarief.accommodatie.col.hoog.${berekening}`),
          align: "right" as const,
          cell: (r: HistorieRegel) => (r.bedragHoog != null ? formatEuro(r.bedragHoog, locale) : "—"),
        }]
      : []),
    { header: t("btw.kolom"), align: "right", cell: (r) => btwWeergave(btwCodes, r.btwCodeId) },
    { header: t("pensionTarief.hist.grootboek"), cell: (r) => labelVan(opties.grootboekRekeningen, r.grootboekRekeningId) },
    { header: t("pensionTarief.hist.kostenplaats"), cell: (r) => labelVan(opties.kostenPlaatsen, r.kostenPlaatsId) },
    { header: t("pensionTarief.hist.projectcode"), cell: (r) => labelVan(opties.projectCodes, r.projectCodeId) },
    { header: t("pensionTarief.hist.reden"), cell: (r) => r.reden ?? "—" },
    {
      header: t("pensionTarief.hist.status"),
      cell: (r) => <StatusBadge tone={toon(r.status)}>{t(`pensionTarief.hist.status.${r.status}`)}</StatusBadge>,
    },
  ];

  return (
    <Modal
      open
      size="wide"
      title={t("pensionTarief.accommodatie.dialoog.titel", { args: [rij.naam] })}
      onClose={() => { if (bewerkt === null) onSluit(); }}
      footer={
        <Button variant="secondary" onClick={onSluit}>
          {t("common.close")}
        </Button>
      }
    >
      <div className="asiel-tarief__form">
        <fieldset className="asiel-tarief__section">
          <legend>{t("pensionTarief.accommodatie.section.huidig")}</legend>
          <div className="pension-tarief__current">
            <div>
              <span className="pension-tarief__current-label">{t(`pensionTarief.accommodatie.col.laag.${berekening}`)}</span>
              <span className="pension-tarief__current-value">
                {rij.dagprijs != null ? formatEuro(rij.dagprijs, locale) : t("pensionTarief.accommodatie.geenPrijs")}
              </span>
            </div>
            {kentSeizoen ? (
              <div>
                <span className="pension-tarief__current-label">{t(`pensionTarief.accommodatie.col.hoog.${berekening}`)}</span>
                <span className="pension-tarief__current-value">
                  {rij.dagprijsHoog != null ? formatEuro(rij.dagprijsHoog, locale) : t("pensionTarief.accommodatie.geenPrijs")}
                </span>
              </div>
            ) : null}
            <div>
              <span className="pension-tarief__current-label">{t("pensionTarief.accommodatie.col.geldigVanaf")}</span>
              <span className="pension-tarief__current-value">{rij.geldigVanaf ? formatDate(rij.geldigVanaf, locale) : "—"}</span>
            </div>
          </div>
        </fieldset>

        <fieldset className="asiel-tarief__section">
          <legend>{t("pensionTarief.accommodatie.section.wijziging")}</legend>
          {melding && (
            <div className="asiel-tarief__notice asiel-tarief__notice--error" role="alert">
              {t(`pensionTarief.accommodatie.fout.${melding}`)}
            </div>
          )}
          {opgeslagen && (
            <div className="asiel-tarief__notice asiel-tarief__notice--success" role="status">
              {t("pensionTarief.accommodatie.opgeslagen")}
            </div>
          )}
          <p className="asiel-tarief__cond-empty">{t("pensionTarief.accommodatie.hint")}</p>
          {btwOntbreekt && (
            <div className="asiel-tarief__field-error" role="alert">
              {t("btw.verplicht")}
            </div>
          )}
          {regels.length > 0 ? (
            <ul className="pension-tarief__plan-lijst">
              {regels.map((r, i) => (
                <li className="pension-tarief__plan-item" key={i} data-testid="prijsregel">
                  <span className="pension-tarief__plan-datum">{formatDate(r.effectdatum, locale)}</span>
                  <span className="pension-tarief__plan-bedrag">{samenvatting(r)}</span>
                  <span className="pension-tarief__plan-bij">{bijschrift(r)}</span>
                  <button
                    type="button"
                    className="asiel-tarief__link"
                    onClick={() => setBewerkt(i)}
                    aria-label={t("pensionTarief.accommodatie.editRow", { args: [formatDate(r.effectdatum, locale)] })}
                  >
                    {t("common.edit")}
                  </button>
                  <button
                    type="button"
                    className="asiel-tarief__link asiel-tarief__cond-remove"
                    onClick={() => setRegels((lijst) => lijst.filter((_, j) => j !== i))}
                    aria-label={t("pensionTarief.accommodatie.remove")}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          <div className="asiel-tarief__row-actions">
            <Button variant="secondary" onClick={() => setBewerkt("nieuw")}>
              {t("pensionTarief.accommodatie.add")}
            </Button>
            {regels.length > 0 && (
              <Button onClick={bewaar} disabled={bezig}>
                {t("pensionTarief.accommodatie.submit")}
              </Button>
            )}
          </div>
        </fieldset>

        <fieldset className="asiel-tarief__section">
          <legend>{t("pensionTarief.accommodatie.section.historie")}</legend>
          <IndeelbareTabel tabelKey="pensiontarief-accommodatie-tarieven-tab-2"
            columns={kolommen}
            rows={historie}
            getRowKey={(r) => String(r.versieId)}
            empty={isLoading ? t("common.loading") : t("pensionTarief.hist.empty")}
          />
        </fieldset>
      </div>
      {bewerkt !== null ? (
        <RegelVenster
          regel={bewerkt === "nieuw" ? legeRegel() : regels[bewerkt]}
          nieuw={bewerkt === "nieuw"}
          berekening={berekening}
          kentSeizoen={kentSeizoen}
          btwCodes={btwCodes}
          opties={opties}
          t={t}
          onSluit={() => setBewerkt(null)}
          onKlaar={(regel) => {
            setBtwOntbreekt(false);
            setRegels((lijst) => (bewerkt === "nieuw"
              ? [...lijst, regel]
              : lijst.map((r, j) => (j === bewerkt ? regel : r))));
            setBewerkt(null);
          }}
        />
      ) : null}
    </Modal>
  );
}

/**
 * Eén prijsregel invullen, in een venster boven het prijsvenster.
 *
 * <p>De velden stonden eerst als losse rij in het prijsvenster zelf. Met een ingangsdatum, een of twee
 * bedragen, btw, drie financiële keuzelijsten en een reden werd dat een rij van acht velden die zich
 * over vier regels vouwde; welk veld bij welke regel hoorde was niet meer te zien. Hier heeft elk veld
 * zijn eigen ruimte, en het prijsvenster houdt een leesbare lijst over van wat er klaarstaat.
 *
 * <p>De regel gaat pas terug naar de lijst als hij compleet is: een ingangsdatum, een bedrag en een
 * btw-code. Een halve regel in de lijst zou pas bij "Prijs opslaan" stuklopen, en dan is niet meer te
 * zien wélke van de vier regels het probleem was.
 */
function RegelVenster({
  regel,
  nieuw,
  berekening,
  kentSeizoen,
  btwCodes,
  opties,
  t,
  onSluit,
  onKlaar,
}: {
  regel: PrijsRegel;
  nieuw: boolean;
  berekening: Prijsberekening;
  kentSeizoen: boolean;
  btwCodes: BtwCode[];
  opties: FinancieleOpties;
  t: T;
  onSluit: () => void;
  onKlaar: (regel: PrijsRegel) => void;
}) {
  const [concept, setConcept] = useState<PrijsRegel>(regel);
  const [fout, setFout] = useState<string | null>(null);
  const zet = (deel: Partial<PrijsRegel>) => setConcept((c) => ({ ...c, ...deel }));

  const bevestig = () => {
    if (concept.effectdatum === "" || getal(concept.bedrag) == null) {
      setFout(t("pensionTarief.accommodatie.regel.onvolledig"));
      return;
    }
    if (concept.btwCodeId === "") {
      setFout(t("btw.verplicht"));
      return;
    }
    onKlaar(concept);
  };

  return (
    <Modal
      open
      title={t(nieuw ? "pensionTarief.accommodatie.regel.nieuw" : "pensionTarief.accommodatie.regel.wijzig")}
      subtitle={t("pensionTarief.accommodatie.regel.uitleg")}
      onClose={onSluit}
      footer={
        <>
          <Button variant="secondary" onClick={onSluit}>{t("common.cancel")}</Button>
          <Button onClick={bevestig}>
            {t(nieuw ? "pensionTarief.accommodatie.regel.voegToe" : "pensionTarief.accommodatie.regel.bijwerken")}
          </Button>
        </>
      }
    >
      <div className="pension-tarief__regel-form">
        <Field
          label={t("pensionTarief.accommodatie.field.effectdatum")}
          type="date"
          value={concept.effectdatum}
          onChange={(e) => zet({ effectdatum: e.target.value })}
        />
        <Field
          label={t(`pensionTarief.accommodatie.col.laag.${berekening}`)}
          value={concept.bedrag}
          inputMode="decimal"
          data-testid="regel-bedrag"
          onChange={(e) => zet({ bedrag: e.target.value })}
        />
        {kentSeizoen ? (
          <Field
            label={t(`pensionTarief.accommodatie.col.hoog.${berekening}`)}
            value={concept.bedragHoog}
            inputMode="decimal"
            onChange={(e) => zet({ bedragHoog: e.target.value })}
          />
        ) : null}
        <BtwKeuze
          value={concept.btwCodeId}
          onChange={(v) => zet({ btwCodeId: v })}
          codes={btwCodes}
          missing={false}
          t={t}
        />
        <SelectField
          label={t("pensionTarief.hist.grootboek")}
          value={concept.grootboekRekeningId}
          onChange={(e) => zet({ grootboekRekeningId: e.target.value })}
          options={keuzes(opties.grootboekRekeningen, t("pensionTarief.financieel.geen"))}
        />
        <SelectField
          label={t("pensionTarief.hist.kostenplaats")}
          value={concept.kostenPlaatsId}
          onChange={(e) => zet({ kostenPlaatsId: e.target.value })}
          options={keuzes(opties.kostenPlaatsen, t("pensionTarief.financieel.geen"))}
        />
        <SelectField
          label={t("pensionTarief.hist.projectcode")}
          value={concept.projectCodeId}
          onChange={(e) => zet({ projectCodeId: e.target.value })}
          options={keuzes(opties.projectCodes, t("pensionTarief.financieel.geen"))}
        />
        <Field
          label={t("pensionTarief.accommodatie.field.reden")}
          value={concept.reden}
          onChange={(e) => zet({ reden: e.target.value })}
        />
        {fout ? <p className="asiel-tarief__field-error" role="alert">{fout}</p> : null}
      </div>
    </Modal>
  );
}
